import { firebaseAnalytics } from './firebase.service';
class AnalyticsEventsService {
	// Helper to safely call firebaseAnalytics methods
	private safeLogEvent(eventName: string, params?: Record<string, any>) {
		if (firebaseAnalytics && typeof firebaseAnalytics.logEvent === 'function') {
			try {
				firebaseAnalytics.logEvent(eventName, params);
			} catch (error) {
				console.warn(`[Analytics] Failed to log event ${eventName}:`, error);
			}
		} else {
			console.warn(`[Analytics] firebaseAnalytics not available, skipping event: ${eventName}`);
		}
	}

	// ========== SCREEN VIEWS ==========

	trackPageView() {
		this.safeLogEvent('page_view', {
			content_type: 'page_view',
			item_id: typeof window !== 'undefined' ? window.location?.hash || '/' : '/',
		});
	}

	trackOpenLobby() {
		this.safeLogEvent('game_screen_view', { name: 'lobby' });
	}

	trackOpenProfile(type?: string) {
		this.safeLogEvent('game_screen_view', {
			name: 'profile',
			type: type || 'own',
		});
	}

	trackOpenProfileStatistics(type?: string) {
		this.safeLogEvent('game_screen_view', {
			name: 'profile_statistics',
			type: type || 'own',
		});
	}

	trackOpenTournaments() {
		this.safeLogEvent('game_screen_view', { name: 'tournaments' });
	}

	trackOpenTournamentInfo() {
		this.safeLogEvent('game_screen_view', { name: 'tournament_info' });
	}

	trackOpenTournamentTable() {
		this.safeLogEvent('game_screen_view', { name: 'tournament_table' });
	}

	trackOpenActiveTournaments(entryLevel?: number) {
		this.safeLogEvent('game_screen_view', {
			name: 'tournament_active_tables',
			entry_level: entryLevel,
		});
	}

	trackOpenTournamentSpectate() {
		this.safeLogEvent('game_screen_view', {
			name: 'tournament_table',
			type: 'spectate',
		});
	}

	trackOpenRoom(roomName: string) {
		this.safeLogEvent('game_screen_view', {
			name: 'room',
			room_name: roomName,
		});
	}

	trackOpenTable(roomName?: string, tableId?: number | string) {
		this.safeLogEvent('game_screen_view', {
			name: 'table',
			room_name: roomName,
			table_id: tableId,
		});
	}

	trackOpenRanking(type: string, period?: string) {
		this.safeLogEvent('game_screen_view', {
			name: 'ranking',
			type: type,
			period: period,
		});
	}

	trackOpenLogin() {
		this.safeLogEvent('game_screen_view', { name: 'login' });
	}

	trackOpenLostPassword() {
		this.safeLogEvent('game_screen_view', { name: 'lost_password' });
	}

	// ========== USER ACTIONS ==========

	trackUserLogin(method: string = 'email') {
		this.safeLogEvent('login', { method });
	}

	trackUserLogout() {
		this.safeLogEvent('logout', {});
	}

	// ========== SHOP & MONETIZATION ==========

	trackOpenShop() {
		console.log('opening shop');
		this.safeLogEvent('open_shop', {});
	}

	trackBeginCheckout(packageId: string) {
		this.safeLogEvent('begin_checkout', {
			items: [
				{
					id: packageId,
					name: packageId,
					quantity: 1,
				},
			],
		});
	}

	trackCheckoutProgress(packageId: string) {
		this.safeLogEvent('checkout_progress', {
			items: [
				{
					id: packageId,
					name: packageId,
					quantity: 1,
				},
			],
		});
	}

	trackSetCheckoutOption(provider: string) {
		this.safeLogEvent('set_checkout_option', {
			checkout_step: 1,
			checkout_option: 'payment_method',
			value: provider,
		});
	}

	trackPurchase(params: { id: string; price: number; currency: string }) {
		this.safeLogEvent('purchase', {
			transaction_id: Math.floor((Math.random() * 1000000000) % 1000000000),
			value: params.price,
			currency: params.currency,
			items: [
				{
					id: params.id,
					name: params.id,
					quantity: 1,
					price: params.price,
				},
			],
		});
	}

	// ========== BONUS & MINI GAMES ==========

	trackOpenBonusGames() {
		this.safeLogEvent('open_bonus_games', {});
	}

	trackCollectDailyBonus() {
		this.safeLogEvent('collect_daily_bonus', {});
	}

	trackOpenMiniGame(gameName: 'black_and_red' | 'scratch' | 'slot') {
		this.safeLogEvent('open_mini_game', { name: gameName });
	}

	trackOpenJackpotHistory() {
		this.safeLogEvent('open_jackpot_history', {});
	}

	// ========== PROGRESSION ==========

	trackLevelUp(level: number) {
		this.safeLogEvent('level_up', { level });
	}

	// ========== OTHER ==========

	trackOpenHiddenShop() {
		this.safeLogEvent('open_hidden_shop', {});
	}

	trackOpenNotifications() {
		this.safeLogEvent('open_notifications', {});
	}

	trackTournamentRegister(page: string) {
		this.safeLogEvent('tournament_register', { page });
	}

	trackTournamentUnRegister(page: string) {
		this.safeLogEvent('tournament_unregister', { page });
	}

	trackOpenLobbyMenu(name: string) {
		this.safeLogEvent('open_lobby_menu', { name });
	}

	trackOpenSettings() {
		this.safeLogEvent('open_settings', {});
	}

	trackOpenFacebookInvite() {
		this.safeLogEvent('open_facebook_invite', {});
	}

	trackOpenDataControl() {
		this.safeLogEvent('open_data_control', {});
	}

	trackOpenRules() {
		this.safeLogEvent('open_rules', {});
	}

	trackOpenHelp() {
		this.safeLogEvent('open_help', {});
	}

	trackEngageModalShown(name: string) {
		this.safeLogEvent('engage_modal_shown', { name });
	}

	trackEngageModalClick(name: string) {
		this.safeLogEvent('engage_modal_click', { name });
	}

	// ========== WEB APP INSTALL (PWA) ==========

	/**
	 * The install nudge's funnel: `shown` -> `install` (tap) -> `accepted` | `dismissed`
	 * (native dialog), or `not_now` | `never_show`; `installed` when the browser reports it.
	 * `method` says which nudge it was: the Chromium dialog or the iOS instructions.
	 */
	trackPwaInstallNudge(
		action: 'shown' | 'install' | 'accepted' | 'dismissed' | 'not_now' | 'never_show' | 'installed',
		method: 'native' | 'manual-ios',
	) {
		this.safeLogEvent('pwa_install_nudge', { action, method });
	}

	trackEngageModalPurchase(params: { id: string; price: number; currency: string }) {
		this.safeLogEvent('engage_modal_purchase', {
			id: params.id,
			name: params.id,
			quantity: 1,
			price: params.price,
			currency: params.currency,
		});
	}
}

export const analyticsEvents = new AnalyticsEventsService();
